import {Typography, Form, Button, Result} from 'antd';
import { useEffect, FC, useState } from 'react';

import { Paper } from 'componets/ui';
import { CodeField, FormItem } from 'componets/form';
import { RegistrationLayout } from 'componets/layouts';
import { Timer } from 'componets/common';

import { CODE_LENGTH, Rules, TIMER_STORAGE } from 'utils/constants';
import { removeFromStorage, setTimerInStorage } from 'utils/localstorage';

import { useAppDispatch, useAppSelector, useCodeForm, useTranslate } from 'hooks';

import { confirmEmail, resendConfirmationEmail } from 'features/registration/actions';
import { leaveRegistration } from 'features/registration/registrationSlice';

import Utils from 'utils/utils';

import { ReactComponent as ArrowBack } from 'assets/icons/arrow-back.svg';

import styles from '../index.module.scss';
import {useTranslation} from "react-i18next";

const TIMER_SECONDS = 90;

export const VerifyEmail: FC = () => {
	const t = useTranslate([
		'verifyEmailText',
		'verifyEmailLabelText',
		'didNotGetCodeText',
		'resendItText',
		'errorInvalidCodeText',
		'backText',
	]);

	const { t: trans } = useTranslation();

	const user = useAppSelector((state) => state.registration.entity);
	const [isExpired, setIsExpired] = useState(false);
	const [expiredText, setExpiredText] = useState('');
	const [isTimer, setIsTimer] = useState(false);
	const [seconds, setSeconds] = useState(0);

	const dispatch = useAppDispatch();

	useEffect(() => {
		Utils.setTimerInStorage({
			storageName: TIMER_STORAGE,
			seconds: TIMER_SECONDS,
			onStartTimer: () => setIsTimer(true),
			setSeconds: setSeconds,
		});
	}, []);

	const { fields, disabled, setFields, setIsValid, isValid, setDisabled, code } = useCodeForm();

	useEffect(() => {
		if (isValid) {
			setDisabled(true);
			dispatch(confirmEmail({ uuid: user.uuid, code: code.value }))
				.unwrap()
				.then(() => {
					removeFromStorage(TIMER_STORAGE);
					if (sessionStorage.getItem('email')) {
						sessionStorage.removeItem('email');
					}
				})
				.catch((e) => {
					const expired = (e?.errors || []).find((el: any) => el.code === 400);
					if (expired) {
						setIsExpired(true)
						setExpiredText(trans(e.message));
					}
					setFields([{ ...code, errors: [trans(e.message || 'errorInvalidCodeText')] }]);
					setDisabled(false);
					setIsValid(false);
				});
		}
	}, [code, dispatch, isValid, setDisabled, setFields, setIsValid, t.errorInvalidCodeText, user]);

	const onResendCode = () => {
		const value = { uuid: user.uuid };
		dispatch(resendConfirmationEmail(value))
			.unwrap()
			.then(() => {
				setTimerInStorage(TIMER_SECONDS, TIMER_STORAGE);
				setSeconds(TIMER_SECONDS);
				setIsTimer(true);
			})
			.catch(() => {});
	};

	return (
		<RegistrationLayout>
			<Paper styleName={styles.blockForm} top={<>1 / 2</>}>
				{!isExpired ? (
					<>
						<Typography.Title level={4}>{t.verifyEmailText}</Typography.Title>

						<Typography.Text type="secondary">
							{t.verifyEmailLabelText} <Typography.Text>{user.email}</Typography.Text>
						</Typography.Text>

						<Form onFieldsChange={setFields} fields={fields}>
							<FormItem className="code" name="code" rules={[Rules.REQUIRED]}>
								<CodeField name="code" fields={CODE_LENGTH} disabled={disabled} />
							</FormItem>
						</Form>

						<Typography.Text type="secondary" className={styles.resend}>
							{t.didNotGetCodeText}&nbsp;
							{isTimer && (
								<Timer
									seconds={seconds}
									onEnd={() => {
										setIsTimer(false);
										setSeconds(0);
									}}
									render={(time: number) => <Typography.Text>{time}s </Typography.Text>}
								/>
							)}
							<Button
								type="link"
								disabled={isTimer}
								className={styles.resendButton}
								onClick={onResendCode}
							>
								{t.resendItText}
							</Button>
						</Typography.Text>

						<div className="modal-back">
							<button
								className="modal-backButton"
								type="button"
								onClick={() => {
									sessionStorage.setItem('email', user.email);
									dispatch(leaveRegistration());
								}}
							>
								<ArrowBack />
								{t.backText}
							</button>
						</div>
					</>
				) : (
					<Result
						status="warning"
						title={expiredText}
						extra={
							<Button
								type="primary"
								key="console"
								onClick={() => {
									sessionStorage.setItem('email', user.email);
									dispatch(leaveRegistration());
								}}
							>
								{t.backText}
							</Button>
						}
					/>
				)}
			</Paper>
		</RegistrationLayout>
	);
};
